import { JSX, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaCopy, FaInfoCircle } from 'react-icons/fa';
import { useRouteError } from 'react-router-dom';
import { useLayout } from '../hooks/layout.hook';
import { useNavigation } from '../hooks/navigation.hook';
import { useAppStore } from '../stores/app.store';

export default function ErrorScreen(): JSX.Element {
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const error = useRouteError();
  const detailsRef = useRef<HTMLDialogElement>(null);
  const [copied, setCopied] = useState(false);

  useLayout(() => ({ hideFooter: true, backButton: true, onBack: () => navigate('/') }), [navigate]);

  const componentStack = useAppStore((s) => s.lastComponentStack);
  const details = error
    ? [
        `v${import.meta.env.APP_VERSION} · ${navigator.userAgent}`,
        '',
        formatError(error),
        componentStack && `\nComponent stack:${componentStack}`,
      ]
        .filter(Boolean)
        .join('\n')
    : undefined;

  return (
    <div className="min-h-screen p-5 gap-5 flex flex-col justify-center items-center">
      <div role="alert" className="alert alert-error alert-soft max-w-2xl">
        <span>{t('An unexpected application error occurred, please go back and try again.')}</span>
      </div>

      <div className="flex flex-col items-center gap-2">
        <button className="btn btn-error" onClick={() => navigate('/')}>
          {t('Back')}
        </button>

        {details && (
          <button className="btn btn-ghost btn-sm gap-2" onClick={() => detailsRef.current?.showModal()}>
            <FaInfoCircle />
            {t('Technical details')}
          </button>
        )}
      </div>

      {details && (
        <dialog ref={detailsRef} className="modal">
          <div className="modal-box max-w-2xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-lg">{t('Technical details')}</h3>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  navigator.clipboard.writeText(details);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                {copied ? <FaCheck /> : <FaCopy />}
              </button>
            </div>
            <pre className="text-xs whitespace-pre-wrap break-all bg-base-200 p-3 rounded max-h-96 overflow-auto">
              {details}
            </pre>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button>close</button>
          </form>
        </dialog>
      )}
    </div>
  );
}

function formatError(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}${error.stack ? `\n\n${error.stack}` : ''}`;
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error, Object.getOwnPropertyNames(error as object), 2);
  } catch {
    return String(error);
  }
}
